import React, { useState } from 'react';
import { connect } from 'react-redux';
import { AnyAction, Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';
import * as modalWindowActions from 'root/modal/modalWindowActions';
import * as Sentry from '@sentry/react';
import Spinner from 'components/LoadingSpinner';
import { getErrorKeyFromApiError } from 'myParkdUtils/errorRendering';
import { ApiErrorResponse } from 'types';


export const mapApiErrorKeyToTranslationKey = (apiErrorKey: string): string => {
    switch (apiErrorKey) {
        // stop parking session
        case 'PARKING_PROVIDER_DOWN':
            return 'API_ERROR_STOP_SESSION_PARKING_PROVIDER_DOWN';
        case 'SESSION_NOT_ACTIVE':
            return 'API_ERROR_STOP_SESSION_SESSION_NOT_ACTIVE';
        case 'INSUFFICIENT_RIGHTS':
            return 'API_ERROR_STOP_SESSION_INSUFFICIENT_RIGHTS';
        case 'UNKNOWN_SESSION':
            return 'API_ERROR_STOP_SESSION_UNKNOWN_SESSION';
        case 'UNKNOWN_USER':
            return 'API_ERROR_STOP_SESSION_UNKNOWN_USER';
        default:
            // fallback
            Sentry.captureMessage(`unhandled apiErrorKey: ${apiErrorKey} found in mapApiErrorKeyToTranslationKey()`);
            return 'ERROR_SOMETHING_WENT_WRONG_TRY_AGAIN_LATER';
    }
}


export const getTranslationKeyFromApiError = (apiError: ApiErrorResponse): string => {
    const errorKey = getErrorKeyFromApiError(apiError);
    return mapApiErrorKeyToTranslationKey(errorKey);
}

type ActionStatus = {
    actionExecuting: boolean,
    actionSucceeded: boolean,
    actionError: any
}

export const ConfirmationDialogModal = (props: ConfirmationDialogProps) => {
    const [actionStatus, setActionStatus] = useState<ActionStatus>({
        actionExecuting: false,
        actionSucceeded: false,
        actionError: null,
    });

    async function onConfirm() {
        try {
            setActionStatus({
                actionExecuting: true,
                actionSucceeded: false,
                actionError: null,
            });
            await props.actionOnConfirm();
            props.actionOnCompleted && props.actionOnCompleted();
            setActionStatus({
                actionExecuting: false,
                actionSucceeded: true,
                actionError: null,
            });
        } catch (error) {
            setActionStatus({
                actionExecuting: false,
                actionSucceeded: false,
                actionError: error,
            });
            props.actionOnError && props.actionOnError(error);
        }
    }


    return (
        <div className="panel radius-rectangle full-width-form confirmation-modal">
            <div className="panel-header">
                <h1 className="bold-title">{props.modalTitle}</h1>
            </div>
            <div className="panel-content">
                {actionStatus.actionExecuting || actionStatus.actionSucceeded || actionStatus.actionError ? (
                    <div>
                        {actionStatus.actionExecuting && <Spinner isInline={true} color="blue" message={props.loadingMessage} />}
                        {actionStatus.actionSucceeded && (
                            <p data-testid="confirmation-modal-success-message">
                                {props.successMessage}
                            </p>
                        )}
                        {actionStatus.actionError && (
                            <p className='txt-error'>
                                <FormattedMessage id={getTranslationKeyFromApiError(actionStatus.actionError)} />
                            </p>
                        )}
                        <button type="button" className="btn-blue btn-full-width offset-top-15" onClick={props.closeModal}>
                            <FormattedMessage id="GLOBAL_CLOSE" />
                        </button>
                    </div>
                ) : (
                    <React.Fragment>
                        <p>{props.modalText}</p>
                        <div className="row-7">
                            <div className="col-6">
                                <button className="btn-cancel" onClick={props.closeModal}>
                                    <FormattedMessage id="GLOBAL_CANCEL" />
                                </button>
                            </div>
                            <div className="col-6">
                                <button className={props.confirmButtonClass ?? "btn-red"} onClick={onConfirm}>
                                    {props.confirmButtonText}
                                </button>
                            </div>
                        </div>
                    </React.Fragment>
                )}
            </div>
        </div>
    );
};


type OwnProps = {
    actionOnConfirm: Function,
    actionOnCompleted?: Function,
    actionOnError?: Function,
    modalTitle: string,
    modalText: string,
    confirmButtonText: string,
    confirmButtonClass?: string,
    loadingMessage: string,
    successMessage: string
};
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
export type ConfirmationDialogProps = OwnProps & DispatchProps;

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return bindActionCreators({
        closeModal: modalWindowActions.closeModal,
    }, dispatch);
}

export default connect(null, mapDispatchToProps)(ConfirmationDialogModal);